import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import TranslateIcon from '@mui/icons-material/Translate';
import {
    Alert,
    AlertTitle,
    AppBar,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Popover,
    Stack,
    Tab,
    Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../common/PageHeader';
import ModernBreadcrumbs from '../navigation/ModernBreadcrumbs';
import idmlServiceInstance from '../../services/IdmlService';
import IdmlLanguageManager from './IdmlLanguageManager';
import IdmlPageSpreadEditor from './IdmlPageSpreadEditor';
import IdmlSegmentTable from './IdmlSegmentTable';

const POLL_INTERVAL_MS = 3000;
const KEEP_ALIVE_INTERVAL_MS = 5 * 60 * 1000;

// "Started 2 mins ago" -- re-evaluated on every poll tick (POLL_INTERVAL_MS) while a language is
// in flight, so this stays fresh without its own timer.
const formatElapsed = (dateString) => {
    if (!dateString) return null;
    const diffMs = Date.now() - new Date(dateString).getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return 'started moments ago';
    if (diffMinutes < 60) return `started ${diffMinutes} min${diffMinutes !== 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `started ${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
};

// Plain-language status for people who don't know what "parsing" means.
const BOOK_STAGE_INFO = {
    UPLOADED: { label: 'Getting started', color: 'default', icon: HourglassTopIcon, hint: 'The book was uploaded and is about to be read.' },
    PARSING: { label: 'Reading your book...', color: 'info', icon: HourglassTopIcon, hint: 'We are scanning the file to find all the text. This can take a minute.' },
    PARSED: { label: 'Ready to translate', color: 'success', icon: CheckCircleIcon, hint: 'The book has been read successfully. Add a language below to start translating.' },
    PARSE_FAILED: { label: "Couldn't read this file", color: 'error', icon: ErrorIcon, hint: 'Something went wrong while reading the book.' },
    REPARSING_ASSETS: { label: 'Updating fonts & images...', color: 'info', icon: HourglassTopIcon, hint: 'Matching your uploaded fonts/images against the book. This is usually quick.' },
};

/**
 * Parent editor page (design doc §7): holds the book + segments for the currently selected
 * language, shown via IdmlLanguageManager + IdmlSegmentTable. The page-spread half of this
 * (IdmlPageSpreadEditor) is Milestone 2/3 -- not built yet, so this only wires the table view.
 */
const IdmlBookEditor = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [breadcrumb, setBreadcrumb] = useState([]);
    const [book, setBook] = useState(null);
    const [effectivePermission, setEffectivePermission] = useState('READ');
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [segments, setSegments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionReadOnly, setSessionReadOnly] = useState(false);
    const [sessionActiveEditor, setSessionActiveEditor] = useState(null);
    const [viewTab, setViewTab] = useState('table');
    // Milestone 7 (stored only for now, see design doc): book-level translation guidance editor.
    const [promptAnchorEl, setPromptAnchorEl] = useState(null);
    const [promptDraft, setPromptDraft] = useState('');
    const [promptSaving, setPromptSaving] = useState(false);
    // Attach a "Document Fonts"/"Links" folder after the fact, without re-uploading the .idml --
    // covers both "forgot it at creation time" and the filename-matching bug that used to make
    // every Links-folder image silently fail to resolve.
    const [assetsAnchorEl, setAssetsAnchorEl] = useState(null);
    const [assetFontFiles, setAssetFontFiles] = useState([]);
    const [assetImageFiles, setAssetImageFiles] = useState([]);
    const [assetsSaving, setAssetsSaving] = useState(false);
    const [assetsError, setAssetsError] = useState(null);

    const pollRef = useRef(null);
    const keepAliveRef = useRef(null);
    const sessionLanguageRef = useRef(null);
    // Auto-select the book's first target language only once, on first load -- otherwise this
    // would fire on every poll tick while selectedLanguage is (still) null and stomp a user's
    // deliberate "View Source" click before the next tick even lands.
    const didAutoSelectLanguage = useRef(false);

    const loadBook = useCallback(async () => {
        try {
            const response = await idmlServiceInstance.getBook(id, true);
            setBook(response.book);
            setEffectivePermission(response.effectivePermission || 'READ');
            setError(null);

            if (!didAutoSelectLanguage.current && response.book.targetLanguages?.length > 0) {
                didAutoSelectLanguage.current = true;
                setSelectedLanguage((current) => current ?? response.book.targetLanguages[0]);
            }
            return response.book;
        } catch (err) {
            console.error('Failed to load book:', err);
            setError(err.response?.data?.error || 'Failed to load book');
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    // Initial load + poll while parsing or the selected language is still translating
    useEffect(() => {
        loadBook();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [loadBook]);

    // Folder path this book was opened from, so the breadcrumb bar can take you back to it
    useEffect(() => {
        try {
            const storedBreadcrumbs = JSON.parse(localStorage.getItem('breadcrumbs'));
            setBreadcrumb(Array.isArray(storedBreadcrumbs) ? storedBreadcrumbs : []);
        } catch (err) {
            setBreadcrumb([]);
        }
    }, []);

    useEffect(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
        if (!book) return;

        // Poll while the book itself is parsing, or while ANY target language is mid-flight --
        // not just the one currently selected, since "Manage languages" can kick off a
        // start/retry/retranslate for a language other than the one shown in the toolbar.
        const anyLanguageInFlight = Object.values(book.targetLanguageStatus || {}).some(
            (status) => status?.processStage === 'TRANSLATING' || status?.processStage === 'EXPORTING',
        );
        const shouldPoll = book.processStage === 'PARSING' || book.processStage === 'REPARSING_ASSETS' || anyLanguageInFlight;

        if (shouldPoll) {
            pollRef.current = setInterval(loadBook, POLL_INTERVAL_MS);
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [book, loadBook]);

    // Load segments whenever the selected language or book stage changes.
    // With no language selected, still load segments so Source text can be shown. Also keeps
    // loading (and, via the poll tick, refreshing) segments while TRANSLATING so the table shows
    // live progress instead of being blanked out.
    useEffect(() => {
        if (!book) return;

        idmlServiceInstance
            .getSegments(id, selectedLanguage)
            .then(setSegments)
            .catch((err) => console.error('Failed to load segments:', err));
    }, [book, selectedLanguage, id]);

    // Edit session start/keep-alive/end, gated on WRITE permission
    useEffect(() => {
        if (effectivePermission !== 'WRITE' || !selectedLanguage) {
            return;
        }

        let cancelled = false;

        const startSession = async () => {
            try {
                const result = await idmlServiceInstance.startEditSession(id, selectedLanguage);
                if (cancelled) return;
                if (result.status === 'LOCKED' || result.status === 'DUPLICATE_SESSION') {
                    setSessionReadOnly(true);
                    setSessionActiveEditor(result.activeEditor);
                } else {
                    setSessionReadOnly(false);
                    setSessionActiveEditor(null);
                }
                sessionLanguageRef.current = selectedLanguage;

                if (keepAliveRef.current) clearInterval(keepAliveRef.current);
                keepAliveRef.current = setInterval(() => {
                    idmlServiceInstance.keepAliveEditSession(id, selectedLanguage).catch((e) => {
                        console.warn('Keep-alive failed:', e);
                    });
                }, KEEP_ALIVE_INTERVAL_MS);
            } catch (err) {
                console.warn('Could not start idml edit session:', err);
            }
        };

        startSession();

        return () => {
            cancelled = true;
            if (keepAliveRef.current) {
                clearInterval(keepAliveRef.current);
                keepAliveRef.current = null;
            }
            const lang = sessionLanguageRef.current;
            sessionLanguageRef.current = null;
            if (lang) {
                idmlServiceInstance.endEditSession(id, lang).catch(() => {});
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, selectedLanguage, effectivePermission]);

    // Shared onSegmentUpdate (design doc §7): both IdmlSegmentTable and IdmlPageSpreadEditor call
    // this after a PATCH/refit so an edit in either view is immediately reflected in the other.
    const handleSegmentUpdate = (segmentId, updatedSegment) => {
        setSegments((prev) => prev.map((s) => (s.id === segmentId ? updatedSegment : s)));
    };

    // A bulk Re-fit's response doesn't include the individual updated segments, so both views
    // fall back to a plain re-fetch of the segment list after a bulk Re-fit completes.
    const handleSegmentsBulkRefit = async () => {
        if (!selectedLanguage) return;
        try {
            const updated = await idmlServiceInstance.getSegments(id, selectedLanguage);
            setSegments(updated);
        } catch (err) {
            console.error('Failed to reload segments after bulk refit:', err);
        }
    };

    const currentPath = breadcrumb && breadcrumb.length > 0 ? `/${breadcrumb.join('/')}` : '/';

    const handleBreadcrumbNavigate = ({ path }) => {
        const newBreadcrumbs = path.split('/').filter((segment) => segment.length > 0);
        localStorage.setItem('breadcrumbs', JSON.stringify(newBreadcrumbs));
        navigate('/', { state: { fromVideoEdit: true, folderPath: path, refreshNeeded: true } });
    };

    const handleLanguageAdded = (updatedBook) => {
        setBook(updatedBook);
        if (!selectedLanguage) {
            const languages = updatedBook.targetLanguages || [];
            if (languages.length > 0) setSelectedLanguage(languages[languages.length - 1]);
        }
    };

    const handleOpenPromptEditor = (event) => {
        setPromptDraft(book?.translationPrompt || '');
        setPromptAnchorEl(event.currentTarget);
    };

    const handleClosePromptEditor = () => {
        if (promptSaving) return;
        setPromptAnchorEl(null);
    };

    const handleSavePrompt = async () => {
        setPromptSaving(true);
        try {
            const updatedBook = await idmlServiceInstance.setTranslationPrompt(id, promptDraft);
            setBook(updatedBook);
            setPromptAnchorEl(null);
        } catch (err) {
            console.error('Failed to save translation prompt:', err);
        } finally {
            setPromptSaving(false);
        }
    };

    const handleOpenAssetsEditor = (event) => {
        setAssetFontFiles([]);
        setAssetImageFiles([]);
        setAssetsError(null);
        setAssetsAnchorEl(event.currentTarget);
    };

    const handleCloseAssetsEditor = () => {
        if (assetsSaving) return;
        setAssetsAnchorEl(null);
    };

    const handleUploadAssets = async () => {
        if (assetFontFiles.length === 0 && assetImageFiles.length === 0) {
            setAssetsError('Choose a Document Fonts and/or Links folder first');
            return;
        }
        setAssetsSaving(true);
        setAssetsError(null);
        try {
            const updatedBook = await idmlServiceInstance.reparseAssets(id, assetFontFiles, assetImageFiles);
            setBook(updatedBook);
            setAssetsAnchorEl(null);
        } catch (err) {
            console.error('Failed to reparse idml assets:', err);
            setAssetsError(err.response?.data?.error || 'Failed to upload fonts/images');
        } finally {
            setAssetsSaving(false);
        }
    };

    const pageChrome = (children) => (
        <Box sx={{ height: '100vh', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
            <AppBar
                position="static"
                sx={{ display: 'flex', padding: 0, width: '100%', left: 0, right: 0, top: 0, ml: 0, mt: 0, borderRadius: 0 }}
            >
                <Toolbar sx={{ px: { xs: 0.5, sm: 1 }, minHeight: { xs: '48px' }, width: '100%', borderRadius: 0 }}>
                    <Box sx={{ ml: 1, flexGrow: 1 }}>
                        <ModernBreadcrumbs
                            currentPath={currentPath}
                            onNavigate={handleBreadcrumbNavigate}
                            isDarkBackground={true}
                            maxItems={5}
                        />
                        <Typography color="inherit" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <AutoStoriesIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {book?.name || 'Book'}
                        </Typography>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
        </Box>
    );

    if (loading) {
        return pageChrome(
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                <CircularProgress />
            </Box>,
        );
    }

    if (error || !book) {
        return pageChrome(
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error || 'Book not found'}</Alert>
            </Box>,
        );
    }

    // No edit session is established without a target language, so source-only viewing is
    // read-only; likewise while the selected language is still translating (nothing to save yet).
    const languageStage = selectedLanguage ? book.targetLanguageStatus?.[selectedLanguage]?.processStage : null;
    const isReadOnly =
        !selectedLanguage || effectivePermission !== 'WRITE' || sessionReadOnly || languageStage === 'TRANSLATING';
    const stageInfo = BOOK_STAGE_INFO[book.processStage] || BOOK_STAGE_INFO.UPLOADED;
    const StageIcon = stageInfo.icon;

    return pageChrome(
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Frozen header: book name/source/status bar + tabs stay visible while the content below scrolls. */}
            <Box sx={{ px: 2, pt: 2, pb: 0, flexShrink: 0 }}>
                <Paper
                    elevation={0}
                    sx={{
                        px: 2,
                        py: 1,
                        mb: 1.5,
                        borderRadius: 2,
                        background: (theme) =>
                            theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(144,202,249,0.08), rgba(144,202,249,0.02))'
                                : 'linear-gradient(135deg, #eef4ff, #f8fbff)',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.25} rowGap={0.5}>
                        <AutoStoriesIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            {book.name}
                        </Typography>
                        <Chip size="small" variant="outlined" label={book.sourceLanguage} sx={{ fontWeight: 500 }} />
                        <Tooltip title={stageInfo.hint} arrow>
                            <Chip
                                size="small"
                                color={stageInfo.color}
                                icon={<StageIcon sx={{ fontSize: '16px !important' }} />}
                                label={
                                    (book.processStage === 'PARSING' || book.processStage === 'REPARSING_ASSETS') && book.progressMessage
                                        ? book.progressMessage
                                        : stageInfo.label
                                }
                                sx={{ fontWeight: 600 }}
                            />
                        </Tooltip>

                        <Box sx={{ flexGrow: 1 }} />

                        <Tooltip title="Translation guidance for this book (applies to every language; not yet used by machine translation)" arrow>
                            <IconButton size="small" onClick={handleOpenPromptEditor} color={book.translationPrompt ? 'primary' : 'default'}>
                                <TranslateIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Popover
                            open={Boolean(promptAnchorEl)}
                            anchorEl={promptAnchorEl}
                            onClose={handleClosePromptEditor}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            <Box sx={{ p: 2, width: 520 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Translation guidance
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Applies to every target language on this book. This is the prompt sent to the AI translation server; saved now, not yet passed to machine translation.
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={10}
                                    maxRows={20}
                                    placeholder="e.g. Translate formally; keep Sanskrit terms untransliterated"
                                    value={promptDraft}
                                    onChange={(e) => setPromptDraft(e.target.value)}
                                    disabled={promptSaving || effectivePermission !== 'WRITE'}
                                />
                                <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
                                    <Button size="small" onClick={handleClosePromptEditor} disabled={promptSaving}>
                                        Cancel
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={handleSavePrompt}
                                        disabled={promptSaving || effectivePermission !== 'WRITE'}
                                    >
                                        {promptSaving ? <CircularProgress size={16} /> : 'Save'}
                                    </Button>
                                </Stack>
                            </Box>
                        </Popover>

                        {effectivePermission === 'WRITE' && (
                            <>
                                <Tooltip title="Attach a Document Fonts / Links folder without re-uploading the book" arrow>
                                    <IconButton size="small" onClick={handleOpenAssetsEditor}>
                                        <DriveFolderUploadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Popover
                                    open={Boolean(assetsAnchorEl)}
                                    anchorEl={assetsAnchorEl}
                                    onClose={handleCloseAssetsEditor}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    <Box sx={{ p: 2, width: 320 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Document Fonts / Links
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                            Attach the book's own font or image folders after the fact -- no need to re-upload the .idml.
                                        </Typography>
                                        {assetsError && (
                                            <Alert severity="error" sx={{ py: 0, mb: 1 }}>
                                                {assetsError}
                                            </Alert>
                                        )}
                                        <Stack spacing={1}>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                size="small"
                                                disabled={assetsSaving}
                                            >
                                                {assetFontFiles.length > 0
                                                    ? `Document Fonts: ${assetFontFiles.length} file(s)`
                                                    : 'Choose Document Fonts folder'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    webkitdirectory=""
                                                    directory=""
                                                    multiple
                                                    onChange={(e) => setAssetFontFiles(Array.from(e.target.files || []))}
                                                />
                                            </Button>
                                            <Button
                                                component="label"
                                                variant="outlined"
                                                size="small"
                                                disabled={assetsSaving}
                                            >
                                                {assetImageFiles.length > 0
                                                    ? `Links: ${assetImageFiles.length} file(s)`
                                                    : 'Choose Links folder'}
                                                <input
                                                    type="file"
                                                    hidden
                                                    webkitdirectory=""
                                                    directory=""
                                                    multiple
                                                    onChange={(e) => setAssetImageFiles(Array.from(e.target.files || []))}
                                                />
                                            </Button>
                                        </Stack>
                                        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1.5 }}>
                                            <Button size="small" onClick={handleCloseAssetsEditor} disabled={assetsSaving}>
                                                Cancel
                                            </Button>
                                            <Button size="small" variant="contained" onClick={handleUploadAssets} disabled={assetsSaving}>
                                                {assetsSaving ? <CircularProgress size={16} /> : 'Upload'}
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Popover>
                            </>
                        )}

                        <IdmlLanguageManager
                            book={book}
                            selectedLanguage={selectedLanguage}
                            onSelectLanguage={setSelectedLanguage}
                            onLanguageAdded={handleLanguageAdded}
                            readOnly={effectivePermission !== 'WRITE'}
                        />
                    </Stack>

                    {book.processStage === 'PARSE_FAILED' && (
                        <Alert severity="error" sx={{ py: 0, mt: 1 }}>
                            We couldn't read this book: {book.errorMessage || 'Unknown error'}. Try uploading it again.
                        </Alert>
                    )}
                    {sessionReadOnly && (
                        <Alert severity="warning" sx={{ py: 0, mt: 1 }}>
                            {sessionActiveEditor} is editing this right now, so you're just looking (read-only) for the moment.
                        </Alert>
                    )}
                </Paper>

                {!selectedLanguage && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Showing the Source text. Pick or add a language above to start translating this book.
                    </Typography>
                )}

                {selectedLanguage && languageStage === 'TRANSLATING' && (
                    <Alert
                        icon={<CircularProgress size={18} thickness={5} />}
                        severity="info"
                        sx={{ mb: 1, alignItems: 'center' }}
                    >
                        <AlertTitle sx={{ mb: 0 }}>
                            Translating into {selectedLanguage}
                            {formatElapsed(book.targetLanguageStatus?.[selectedLanguage]?.translationStartedAt)
                                ? ` (${formatElapsed(book.targetLanguageStatus?.[selectedLanguage]?.translationStartedAt)})`
                                : '...'}
                        </AlertTitle>
                        This usually takes a few minutes. The text below fills in as it completes.
                    </Alert>
                )}

                <Tabs
                    value={viewTab}
                    onChange={(e, v) => setViewTab(v)}
                    sx={{ mb: 1, minHeight: 36 }}
                >
                    <Tab value="table" label="Text List" sx={{ minHeight: 36 }} />
                    <Tab value="pageSpread" label="Page Preview" sx={{ minHeight: 36 }} />
                </Tabs>
            </Box>

            {/* Scrollable content: only this area scrolls, so the header bar above stays in view. */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 2 }}>
                {viewTab === 'table' && (
                    <IdmlSegmentTable
                        bookId={id}
                        segments={segments}
                        languageCode={selectedLanguage}
                        readOnly={isReadOnly}
                        onSegmentUpdate={handleSegmentUpdate}
                        onSegmentsBulkRefit={handleSegmentsBulkRefit}
                    />
                )}

                {viewTab === 'pageSpread' && (
                    <IdmlPageSpreadEditor
                        bookId={id}
                        languageCode={selectedLanguage}
                        pageCount={book.pages?.length || 1}
                        readOnly={isReadOnly}
                        uploadedFonts={book.uploadedFonts}
                        onSegmentUpdate={handleSegmentUpdate}
                        onSegmentsBulkRefit={handleSegmentsBulkRefit}
                    />
                )}
            </Box>
        </Box>
    );
};

export default IdmlBookEditor;
